import { MyTmpVpnClient } from '@mytmpvpn/mytmpvpn-client/client'
import { CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'
import { AuthUser } from 'aws-amplify/auth'
import { logger } from '../logging'

const isMock = process.env.MYTMPVPN_USEMOCKS === "true"

/**
 * Checks if the client is ready for authenticated API calls
 * @param client The MyTmpVpnClient instance
 * @returns true if client has valid session, false otherwise
 */
export function isClientAuthenticated(client: MyTmpVpnClient): boolean {
    return client !== null &&
        client !== undefined &&
        client.getUser() !== undefined &&
        client.getSession() !== undefined
}

/**
 * Converts Amplify auth session to CognitoUserSession and CognitoUser
 * @returns Object with CognitoUser and CognitoUserSession or null if not authenticated
 */
export async function getCognitoUserAndSession(): Promise<{ user: CognitoUser, session: CognitoUserSession } | null> {
    try {
        if (isMock) {
            // For mock environment, create mock objects
            const mockUserPool = new CognitoUserPool({
                UserPoolId: 'us-east-1_mockUserPool',
                ClientId: 'mock-client-id'
            })

            const mockUser = new CognitoUser({
                Username: 'mockuser',
                Pool: mockUserPool
            })

            const mockSession = new CognitoUserSession({
                IdToken: new CognitoIdToken({ IdToken: 'mock-id-token' }),
                AccessToken: new CognitoAccessToken({ AccessToken: 'mock-access-token' }),
                RefreshToken: new CognitoRefreshToken({ RefreshToken: 'mock-refresh-token' })
            })

            return {
                user: mockUser,
                session: mockSession
            }
        } else {
            // For real environment, use Amplify v6 APIs
            const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth')

            const [session, currentUser] = await Promise.all([
                fetchAuthSession(),
                getCurrentUser()
            ])

            if (!session.tokens || !currentUser) {
                return null
            }

            const { idToken, accessToken } = session.tokens
            const refreshToken = (session.tokens as any).refreshToken // Type assertion for refresh token

            if (!idToken || !accessToken) {
                return null
            }

            // Create Cognito token objects
            const cognitoIdToken = new CognitoIdToken({ IdToken: idToken.toString() })
            const cognitoAccessToken = new CognitoAccessToken({ AccessToken: accessToken.toString() })
            const cognitoRefreshToken = refreshToken
                ? new CognitoRefreshToken({ RefreshToken: refreshToken.toString() })
                : undefined

            // Create CognitoUserSession
            const cognitoUserSession = new CognitoUserSession({
                IdToken: cognitoIdToken,
                AccessToken: cognitoAccessToken,
                RefreshToken: cognitoRefreshToken
            })

            // Get user pool configuration from the session
            const userPoolId = idToken.payload.iss?.split('/').pop()
            const clientIdRaw = idToken.payload.aud
            const clientId = Array.isArray(clientIdRaw) ? clientIdRaw[0] : clientIdRaw

            if (!userPoolId || !clientId) {
                logger.error('Missing user pool ID or client ID in token')
                return null
            }

            // Create a CognitoUserPool instance
            const userPool = new CognitoUserPool({
                UserPoolId: userPoolId,
                ClientId: clientId
            })

            // Create CognitoUser
            const cognitoUser = new CognitoUser({
                Username: currentUser.username,
                Pool: userPool
            })

            return {
                user: cognitoUser,
                session: cognitoUserSession
            }
        }
    } catch (error) {
        logger.error('Error getting Cognito user and session:', error)
        return null
    }
}

/**
 * @deprecated Use getCognitoUserAndSession instead
 */
export async function getCognitoUserSession(): Promise<CognitoUserSession | null> {
    const result = await getCognitoUserAndSession()
    return result?.session || null
}

export async function populateClientWithSesion(client: MyTmpVpnClient, user: AuthUser): Promise<void> {
    logger.log(`Fetching session for user: ${JSON.stringify(user)}`)

    try {
        const cognitoAuth = await getCognitoUserAndSession()
        if (!cognitoAuth) {
            logger.warn(`No cognito auth found, skipping session setting`)
            throw new Error('Failed to get Cognito authentication session')
        }

        logger.log(`Setting user/session: ${JSON.stringify([cognitoAuth.user, cognitoAuth.session])}`)
        // Use the modernized client that accepts v6 types directly
        client.setUserSession(cognitoAuth.user, cognitoAuth.session)
        logger.debug('Client session set successfully')
    } catch (error) {
        logger.error('Error populating client with session:', error)
        throw error
    }
}